import { memo, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslate } from '@tolgee/react';
import { message } from 'antd';
import { useRequest, useSize } from 'ahooks';
import { initEcharts } from 'lib/echarts';
import dayjs from 'lib/dayjs';
import { useTypeLabel } from 'views/Todo/hooks/useLabel';
import { getTodoCreateTrend } from './api';
import { useCustomTypeContext } from './CutstomTypeContext';

const Trend = memo((props: { values: Todo.Data.Params }) => {
  const { t } = useTranslate();
  const { values } = props;

  const { data } = useRequest<Todo.Data.Trend>(() => getTodoCreateTrend(values), {
    refreshDeps: [values],
    onError: () => {
      message.error(t('phrase.获取数据失败，请稍后再试'));
    },
  });

  const class1 =
    'h-[56px] py-[17px] pl-[24px] text-[var(--fett-text-primary)] text-[16px] font-medium border-solid border-0 border-b border-[var(--fett-border-default)]';

  return (
    <div className="flex">
      <div className="mr-[16px] bg-[var(--fett-bg-panel)] flex-grow">
        <div className={class1}>{t('ViewDashboard.待办创建趋势')}</div>
        <BarBrush values={data?.dailyCountMap} types={values.types} />
      </div>
      <div className="bg-[var(--fett-bg-panel)] w-[426px] flex-shrink-0">
        <div className={class1}>{t('ViewDashboard.待办创建分布')}</div>
        <Pie values={data?.typeCount} />
      </div>
    </div>
  );
});

const getColorRandom = (() => {
  const colorMap: Record<any, any> = {
    0: '#73A0FA',
    1: '#73DEB3',
    2: '#7585A2',
    6: '#A285D2',
  };
  return (each: any) => {
    if (!colorMap[each]) {
      colorMap[each] = `#${Math.random().toString(16).slice(2, 8)}`;
    }
    return colorMap[each];
  };
})();

const BarBrush = memo((props: { values: Todo.Data.Trend['dailyCountMap']; types: number[] }) => {
  const { values, types } = props;
  const chartDomRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts>();
  const { width } = useSize(chartDomRef) || {};
  const { typeLabel } = useTypeLabel();
  const { typeMap, types: customTypes } = useCustomTypeContext();
  const { t } = useTranslate();
  const getName = useCallback(
    (key) => {
      if (String(key) === '-998') {
        return t('custom');
      }
      return typeLabel[key] || typeMap[key] || key;
    },
    [typeLabel, typeMap, t],
  );

  useEffect(() => {
    chartRef.current?.resize();
  }, [width]);

  useLayoutEffect(() => {
    chartRef.current = initEcharts(chartDomRef.current!);
  }, []);

  const options = useMemo(() => {
    const patchTypes = types
      ? Array.isArray(types) && types.length === 0
        ? [...customTypes.map((e) => e.id), -998]
        : types
      : [];

    const typeMap = Object.values(values || {}).reduce(
      (acc, cur) => {
        const cm = cur?.countMap || {};
        for (let key in acc) {
          acc[key].push(cm[key] || 0);
        }
        return acc;
      },
      patchTypes.reduce((acc, cur) => {
        acc[cur] = [];
        return acc;
      }, {}),
    );

    return {
      legend: {
        left: 30,
        data: patchTypes
          .filter((each) => typeMap?.[each]?.reduce((acc, cur) => acc + cur, 0) > 0)
          .map((each) => getName(each)),
        type: 'scroll',
      },
      tooltip: {},
      xAxis: {
        data: Object.keys(values || {}).map((each) => dayjs(Number(each)).format('YYYY-MM-DD')),
        name: '',
        axisLine: { onZero: true },
        splitLine: { show: false },
        splitArea: { show: false },
      },
      yAxis: {},
      grid: {
        left: 30,
        bottom: 30,
      },
      series: patchTypes.map((each) => {
        return {
          name: getName(each),
          itemStyle: {
            color: getColorRandom(each),
          },
          type: 'bar',
          stack: 'all',
          barMaxWidth: 20,
          data: typeMap[each],
        };
      }),
    };
  }, [getName, values, types, customTypes]);

  useEffect(() => {
    chartRef.current?.setOption(options, true);
  }, [options]);

  return (
    <div className="px-[24px] py-[12px]">
      <div ref={chartDomRef} className="h-[400px]"></div>
    </div>
  );
});

const Pie = memo((props: { values: Todo.Data.Trend['typeCount'] }) => {
  const { values } = props;
  const chartDomRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts>();
  const { width } = useSize(chartDomRef) || {};
  const { typeLabel } = useTypeLabel();
  const { typeMap } = useCustomTypeContext();
  const { t } = useTranslate();
  const getName = useCallback(
    (key) => {
      if (String(key) === '-998') {
        return t('custom');
      }
      return typeLabel[key] || typeMap[key] || key;
    },
    [typeLabel, typeMap, t],
  );

  useEffect(() => {
    chartRef.current?.resize();
  }, [width]);

  useLayoutEffect(() => {
    chartRef.current = initEcharts(chartDomRef.current!);
  }, []);

  const options = useMemo(() => {
    const data = Object.entries(values?.countMap || {}).map(([key, value]) => {
      return {
        name: getName(key),
        value,
        key,
      };
    });
    return {
      tooltip: {
        trigger: 'item',
      },
      legend: {
        top: 0,
        left: 'center',
        type: 'scroll',
      },
      series: [
        {
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          label: {
            show: false,
          },
          itemStyle: {
            color: (each) => {
              return getColorRandom(each.data.key);
            },
          },
          labelLine: {
            show: false,
          },
          data,
        },
      ],
    };
  }, [values, getName]);

  useEffect(() => {
    chartRef.current?.setOption(options);
  }, [options]);

  return (
    <div className="px-[24px] py-[12px]">
      <div ref={chartDomRef} className="h-[400px]"></div>
    </div>
  );
});

export default Trend;
